import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { getUTMs } from "../../hooks/useUTMs";
import ConsentCheckboxes, { honeypotValue } from "./ConsentCheckboxes";
import {
  Gift,
  CheckCircle2,
  ArrowLeft,
  Loader2,
  Sparkles,
  ShieldCheck,
  User,
  Phone,
  Mail,
  RotateCcw,
  AlertTriangle,
  Trophy,
  Star,
  Users,
  UserCheck,
  MapPin,
  ChevronDown,
  OctagonX,
  Download,
  BookOpen
} from "lucide-react";
import { toast } from "sonner";
import { useParams, Link, useNavigate } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const questions = [
  {
    id: "q1",
    stepTitle: "Etapa 1 de 3",
    question: "Como você resume a sua relação com o dinheiro hoje?",
    options: [
      {
        key: "A",
        label: "Meu dinheiro trabalha para construir meu patrimônio.",
        sub: "Foco em multiplicação, investimentos e visão de longo prazo."
      },
      {
        key: "B",
        label: "Meu dinheiro só serve para pagar os boletos do mês.",
        sub: "Foco na rotina, contas recorrentes e dificuldade em guardar/acumular."
      }
    ]
  },
  {
    id: "q2",
    stepTitle: "Etapa 2 de 3",
    question: "O que mais impede o seu crescimento financeiro hoje?",
    options: [
      {
        key: "A",
        label: "A falta de planejamento e clareza.",
        sub: "Não ter uma rota clara ou estratégia definida para alavancar."
      },
      {
        key: "B",
        label: "Os juros abusivos na hora de buscar crédito.",
        sub: "Custo alto dos financiamentos e crédito bancário tradicional."
      },
      {
        key: "C",
        label: "O medo de cair em promessas financeiras milagrosas.",
        sub: "Insegurança com riscos desnecessários e consultorias duvidosas."
      }
    ]
  },
  {
    id: "q3",
    stepTitle: "Etapa 3 de 3",
    question: "Qual é o seu principal objetivo financeiro nos próximos 2 anos?",
    options: [
      {
        key: "A",
        label: "Comprar ou quitar meu imóvel.",
        sub: "Sair do aluguel, dar entrada ou eliminar a dívida da casa própria."
      },
      {
        key: "B",
        label: "Aumentar minha renda e liberdade financeira.",
        sub: "Fazer o dinheiro trabalhar por mim e ter mais opções de vida."
      },
      {
        key: "C",
        label: "Organizar as dívidas e reconstruir minha vida financeira.",
        sub: "Sair do vermelho e voltar a ter controle sobre as finanças."
      },
      {
        key: "D",
        label: "Investir em um negócio próprio ou expandir o que já tenho.",
        sub: "Capital de giro, expansão ou abertura de novo empreendimento."
      }
    ]
  }
];

export default function PartnerCapture() {
  const { slug } = useParams();
  const [campanha, setCampanha] = useState(null);
  const [loadingCampanha, setLoadingCampanha] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({ q1: "", q2: "", q3: "" });
  const [leadData, setLeadData] = useState({ name: "", phone: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [duplicado, setDuplicado] = useState(false);
  const [ticketNumber, setTicketNumber] = useState("");
  const [countdown, setCountdown] = useState(8);
  const [lgpdAccepted, setLgpdAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const navigate = useNavigate();

  // Total de etapas: 3 perguntas + 1 formulário = 4
  const TOTAL_STEPS = 4;

  const triggerDownload = () => {
    const link = document.createElement("a");
    link.href = "/downloads/O_Codigo_da_Riqueza.pdf";
    link.download = "O_Codigo_da_Riqueza_com_Consorcio.pdf";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Download do E-book iniciado com sucesso!");
  };

  useEffect(() => {
    async function fetchCampanha() {
      try {
        const res = await axios.get(`${API}/campanhas/${slug}`);
        setCampanha(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoadingCampanha(false);
      }
    }
    if (slug) fetchCampanha();
  }, [slug]);

  // Redireciona para o site principal com flag após conclusão
  useEffect(() => {
    if (!submitted) return;
    setCountdown(8);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate("/?concluiu_quiz=1");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [submitted, navigate]);

  const handleSelectOption = (questionKey, optionKey, optionLabel) => {
    setAnswers((prev) => ({ ...prev, [questionKey]: `${optionKey}) ${optionLabel}` }));
    setTimeout(() => setStep((prev) => prev + 1), 280);
  };

  const formatPhone = (value) => {
    const n = value.replace(/\D/g, "");
    if (n.length <= 2) return n;
    if (n.length <= 7) return `(${n.slice(0, 2)}) ${n.slice(2)}`;
    return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    setLeadData((prev) => ({ ...prev, phone: formatPhone(e.target.value) }));
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadData.name.trim()) { toast.error("Por favor, preencha seu nome completo."); return; }
    if (leadData.phone.replace(/\D/g, "").length < 10) {
      toast.error("Por favor, insira um WhatsApp válido com DDD (ex: 51 99999-9999).");
      return;
    }
    if (!lgpdAccepted) {
      toast.error("Marque a autorização de contato para validar sua participação.");
      return;
    }
    const rawEmail = leadData.email.trim();
    if (rawEmail && !rawEmail.includes("@")) {
      toast.error("Por favor, insira um e-mail válido (ex: seu@email.com).");
      return;
    }

    setLoading(true);

    const surveyMessage = [
      `📋 PESQUISA - PARCEIRO: ${campanha?.nome_parceiro || slug}`,
      `🎁 PRÊMIO: ${campanha?.premio || ""}`,
      "----------------------------------------",
      `1. Relação com o dinheiro: ${answers.q1 || "Não respondida"}`,
      `2. O que impede o crescimento: ${answers.q2 || "Não respondida"}`,
      `3. Objetivo financeiro: ${answers.q3 || "Não respondida"}`,
      "----------------------------------------",
      "LGPD: Consentimento de uso de dados autorizado pelo participante.",
      `Origem: Parceiro · ${campanha?.nome_parceiro || slug}`
    ].join("\n");

    const finalEmail = rawEmail || `lead@parceiro-${slug}.com.br`;

    try {
      await axios.post(`${API}/leads`, {
        name: leadData.name.trim(),
        phone: leadData.phone.trim(),
        email: finalEmail,
        investment_range: "A definir",
        message: surveyMessage,
        source: `parceiro_${slug}`,
        partner_slug: slug,
        lgpd_accepted: true,
        marketing_consent: marketingAccepted,
        utm: getUTMs(),
        website: honeypotValue(e)
      });

      const ticket = `PARC-${Math.floor(100000 + Math.random() * 900000)}`;
      setTicketNumber(ticket);
      setSubmitted(true);
      toast.success("Participação confirmada com sucesso!");

      if (window.gtag) window.gtag("event", "Lead", { event_category: "parceiro_capture", event_label: slug });
      if (window.fbq) window.fbq("track", "Lead", { value: 0, currency: "BRL" });
    } catch (err) {
      // 409 = telefone já cadastrado nesta campanha
      if (err.response?.status === 409) {
        setDuplicado(true);
        return;
      }
      let msg = "Erro ao registrar participação. Tente novamente.";
      if (err.response?.status === 502) msg = "Servidor em reinicialização. Tente em alguns segundos.";
      else if (err.response?.data?.detail) {
        const d = err.response.data.detail;
        if (typeof d === "string") msg = d;
        else if (Array.isArray(d) && d[0]?.msg) msg = d[0].msg;
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAnswers({ q1: "", q2: "", q3: "" });
    setLeadData({ name: "", phone: "", email: "" });
    setStep(1);
    setSubmitted(false);
    setTicketNumber("");
    toast.info("Pronto para o próximo participante!");
  };

  // ── Loading state
  if (loadingCampanha) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="animate-spin text-primary" size={40} />
          <p className="text-muted-foreground text-sm">Carregando campanha...</p>
        </div>
      </div>
    );
  }

  // ── Not found state
  if (notFound) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="text-destructive" size={32} />
          </div>
          <h1 className="font-display text-2xl font-light mb-2">Campanha não encontrada</h1>
          <p className="text-muted-foreground text-sm mb-6">
            O link que você acessou pode estar incorreto ou a campanha foi desativada.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 text-primary hover:underline text-sm">
            Voltar ao site
          </Link>
        </div>
      </div>
    );
  }

  const progressPct = Math.min(((step - 1) / TOTAL_STEPS) * 100, 100);

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col selection:bg-primary/20 selection:text-primary">
      {/* Header */}
      <header className="border-b border-border/60 bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container max-w-4xl h-16 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2">
              <span className="text-gold-gradient font-display text-xl font-bold tracking-tight">
                Juliano Wagner
              </span>
              <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-muted-foreground border-l border-border/80 pl-2">
                Consultoria Patrimonial
              </span>
            </Link>
          </div>

          <div className="inline-flex items-center gap-1.5 bg-primary/10 border border-primary/30 px-3 py-1 rounded-full text-xs font-medium text-primary">
            <Gift size={13} className="text-primary" />
            <span className="truncate max-w-[140px] sm:max-w-none">{campanha?.nome_parceiro}</span>
          </div>
        </div>

        {/* Progress bar */}
        {!submitted && (
          <div className="w-full bg-border/40 h-1">
            <div
              className="h-1 transition-all duration-500 ease-out"
              style={{ width: `${progressPct}%`, background: "var(--gold-gradient)" }}
            />
          </div>
        )}
      </header>

      {/* Prize Banner */}
      {!submitted && (
        <div className="bg-primary/10 border-b border-primary/20 py-3 px-4">
          <div className="container max-w-4xl flex items-center justify-center gap-2 text-sm">
            <Trophy size={16} className="text-primary shrink-0" />
            <span className="text-foreground">
              <strong className="text-gold-gradient">Prêmio:</strong>{" "}
              {campanha?.premio}
            </span>
            <Star size={14} className="text-primary/50 shrink-0" />
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 md:p-8">
        <div className="w-full max-w-2xl mx-auto">
          <AnimatePresence mode="wait">

            {/* ── Tela de telefone duplicado (409) */}
            {duplicado ? (
              <motion.div
                key="duplicado"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-card-gradient border border-destructive/40 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-48 h-48 bg-destructive/10 rounded-full blur-3xl -z-10" />

                <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-destructive/10 ring-2 ring-destructive/30 text-destructive">
                  <OctagonX size={38} />
                </div>

                <span className="inline-block text-xs uppercase tracking-[0.25em] text-destructive font-mono mb-2">
                  Participação Bloqueada
                </span>
                <h2 className="font-display text-2xl sm:text-3xl font-light mb-3">
                  Este número já está{" "}
                  <span className="italic text-destructive font-normal">cadastrado</span>
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base max-w-md mx-auto mb-6 leading-relaxed">
                  O WhatsApp informado já possui uma participação registrada nesta promoção.
                  Apenas <strong className="text-foreground">uma participação por número</strong> é permitida, conforme o regulamento.
                </p>

                <div className="bg-background/60 border border-border/60 rounded-xl p-4 max-w-sm mx-auto mb-6 text-left space-y-2">
                  <p className="text-xs font-semibold text-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
                    <ShieldCheck size={13} className="text-primary" /> Regra 01 — Participação Única
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    Só é permitida uma (1) participação por número de WhatsApp. Entradas duplicadas
                    não serão contabilizadas para a premiação.
                  </p>
                </div>

                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-medium border border-border hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground"
                >
                  Voltar ao site
                </Link>
              </motion.div>
            ) : submitted ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}

                className="bg-card-gradient border border-primary/40 shadow-gold rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-48 h-48 bg-primary/10 rounded-full blur-3xl -z-10" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-primary/5 rounded-full blur-3xl -z-10" />

                <div className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center bg-primary/10 ring-2 ring-primary/40 text-primary">
                  <Trophy size={38} className="animate-bounce" />
                </div>

                <span className="inline-block text-xs uppercase tracking-[0.25em] text-primary font-mono mb-2">
                  Participação Confirmada ✓
                </span>
                <h2 className="font-display text-3xl sm:text-4xl font-light mb-3">
                  Você está concorrendo ao{" "}
                  <span className="italic text-gold-gradient font-normal">prêmio</span>!
                </h2>
                <p className="text-muted-foreground text-sm sm:text-base max-w-md mx-auto mb-4 leading-relaxed">
                  Obrigado, <strong className="text-foreground">{leadData.name}</strong>. Seus dados foram validados.
                  Nosso time entrará em contato via WhatsApp em breve.
                </p>

                <div className="bg-primary/5 border border-primary/20 rounded-xl p-3 max-w-xs mx-auto mb-6">
                  <p className="text-xs text-muted-foreground mb-1">🎁 Prêmio em disputa</p>
                  <p className="font-medium text-sm text-gold-gradient">{campanha?.premio}</p>
                </div>

                {ticketNumber && (
                  <div className="bg-background/80 border border-border/80 rounded-xl p-4 max-w-xs mx-auto mb-6 shadow-inner">
                    <span className="text-[10px] uppercase tracking-widest text-muted-foreground block mb-1">
                      Cupom do Sorteio
                    </span>
                    <span className="font-mono text-xl sm:text-2xl font-bold text-gold-gradient tracking-wider">
                      {ticketNumber}
                    </span>
                  </div>
                )}

                {/* Cortesia Exclusiva - Download do E-book */}
                <div className="bg-background/90 border border-primary/30 rounded-2xl p-5 sm:p-6 mb-6 shadow-gold text-left relative overflow-hidden">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="w-16 h-20 rounded-lg overflow-hidden border border-primary/40 shadow-sm shrink-0 bg-black/40 hidden sm:block">
                      <img
                        src="/images/ebook-cover.png"
                        alt="E-book O Código da Riqueza com Consórcio"
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="inline-flex items-center gap-1.5 bg-primary/15 text-primary text-[10px] font-mono uppercase tracking-widest px-2.5 py-0.5 rounded-full mb-1.5">
                        <BookOpen size={11} /> Cortesia de Agradecimento
                      </div>
                      <h3 className="font-display text-lg sm:text-xl font-medium text-foreground mb-1">
                        E-book: O Código da Riqueza com Consórcio
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                        Aproveite para baixar gratuitamente o guia com métodos práticos para multiplicar seu patrimônio.
                      </p>
                      <button
                        type="button"
                        onClick={triggerDownload}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-medium text-xs sm:text-sm transition-all shadow-gold hover:translate-y-[-1px] cursor-pointer"
                        style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                      >
                        <Download size={15} /> Baixar E-book Gratuito (PDF)
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-border/60 flex flex-col gap-3 items-center">
                  {/* Contador regressivo */}
                  <p className="text-xs text-muted-foreground">
                    Redirecionando para o site em{" "}
                    <span className="text-primary font-bold font-mono">{countdown}s</span>...
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 w-full justify-center">
                    <Link
                      to="/?concluiu_quiz=1"
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm transition-all shadow-gold hover:translate-y-[-1px] cursor-pointer"
                      style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                    >
                      Ir agora para o Site
                    </Link>
                    <button
                      id="btn-proximo-participante"
                      onClick={handleReset}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-medium text-sm border border-border hover:bg-white/5 transition-colors text-muted-foreground hover:text-foreground cursor-pointer"
                    >
                      <RotateCcw size={16} /> Próximo Participante
                    </button>
                  </div>
                </div>
              </motion.div>

            ) : step === 1 ? (
              /* ── Step 1: Questão 1 */
              <QuestionStep
                key="step1"
                question={questions[0]}
                stepLabel="Etapa 1 de 4"
                pct="25%"
                selectedKey={answers.q1}
                onSelect={(key, label) => handleSelectOption("q1", key, label)}
                prize={campanha?.premio}
              />

            ) : step === 2 ? (
              /* ── Step 2: Questão 2 */
              <QuestionStep
                key="step2"
                question={questions[1]}
                stepLabel="Etapa 2 de 4"
                pct="50%"
                selectedKey={answers.q2}
                onSelect={(key, label) => handleSelectOption("q2", key, label)}
                onBack={() => setStep(1)}
                prize={campanha?.premio}
              />

            ) : step === 3 ? (
              /* ── Step 3: Questão 3 */
              <QuestionStep
                key="step3"
                question={questions[2]}
                stepLabel="Etapa 3 de 4"
                pct="75%"
                selectedKey={answers.q3}
                onSelect={(key, label) => handleSelectOption("q3", key, label)}
                onBack={() => setStep(2)}
                prize={campanha?.premio}
              />

            ) : (
              /* ── Step 4: Formulário de dados */
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="bg-card-gradient border border-primary/20 shadow-gold rounded-2xl p-6 sm:p-10"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-primary font-semibold">
                    Etapa 4 de 4 · Cadastro
                  </span>
                  <span className="text-xs text-muted-foreground font-mono">100%</span>
                </div>

                <div className="bg-primary/10 border border-primary/30 rounded-xl p-4 mb-6 flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/20 text-primary shrink-0 mt-0.5">
                    <ShieldCheck size={20} />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground text-sm sm:text-base mb-1">
                      Quase lá! Confirme seus dados para validar o cupom.
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      🎁 Prêmio: <strong className="text-primary">{campanha?.premio}</strong>
                    </p>
                  </div>
                </div>

                <form id="form-cadastro-parceiro" onSubmit={handleLeadSubmit} className="space-y-4">
                  {/* Nome */}
                  <div>
                    <label htmlFor="parceiro-field-1" className="block text-sm text-muted-foreground font-medium mb-1.5">
                      Nome completo <span className="text-primary">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                        <User size={18} />
                      </div>
                      <input
                        id="parceiro-field-1"
                        id="input-nome-parceiro"
                        type="text"
                        required
                        maxLength={100}
                        value={leadData.name}
                        onChange={(e) => setLeadData((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Ex: João da Silva"
                        className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                      />
                    </div>
                  </div>

                  {/* WhatsApp */}
                  <div>
                    <label htmlFor="parceiro-field-2" className="block text-sm text-muted-foreground font-medium mb-1.5">
                      WhatsApp <span className="text-primary">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                        <Phone size={18} />
                      </div>
                      <input
                        id="parceiro-field-2"
                        id="input-whatsapp-parceiro"
                        type="tel"
                        required
                        maxLength={16}
                        value={leadData.phone}
                        onChange={handlePhoneChange}
                        placeholder="(51) 99999-9999"
                        className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                      />
                    </div>
                    <span className="text-xs text-muted-foreground mt-1 block">
                      Usaremos para comunicar o resultado do sorteio.
                    </span>
                  </div>

                  {/* Email (opcional) */}
                  <div>
                    <label htmlFor="parceiro-field-3" className="block text-sm text-muted-foreground font-medium mb-1.5">
                      E-mail <span className="text-muted-foreground/80 text-xs normal-case">(opcional)</span>
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
                        <Mail size={18} />
                      </div>
                      <input
                        id="parceiro-field-3"
                        id="input-email-parceiro"
                        type="email"
                        maxLength={120}
                        value={leadData.email}
                        onChange={(e) => setLeadData((p) => ({ ...p, email: e.target.value }))}
                        placeholder="Ex: cliente@email.com (ou deixe em branco)"
                        className="w-full pl-10 pr-4 py-3.5 rounded-xl bg-background border border-input text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:border-primary/70 focus:ring-1 focus:ring-primary/40 font-body text-base"
                      />
                    </div>
                  </div>

                  <ConsentCheckboxes
                    idPrefix="parceiro"
                    contactText="Autorizo o contato por WhatsApp e e-mail sobre o sorteio e o resultado."
                    contact={lgpdAccepted}
                    onContactChange={setLgpdAccepted}
                    marketing={marketingAccepted}
                    onMarketingChange={setMarketingAccepted}
                  />

                  <button
                    id="btn-validar-participacao"
                    type="submit"
                    disabled={loading}
                    className="w-full py-4 mt-6 rounded-full font-medium text-base transition-all shadow-gold hover:translate-y-[-1px] disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer"
                    style={{ background: "var(--gold-gradient)", color: "#0f0d0a" }}
                  >
                    {loading ? (
                      <><Loader2 className="animate-spin" size={18} /> Validando participação...</>
                    ) : (
                      <><CheckCircle2 size={18} /> Validar Participação no Sorteio</>
                    )}
                  </button>
                </form>

                <div className="flex items-center justify-between pt-6 mt-4 border-t border-border/40 text-xs text-muted-foreground">
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors cursor-pointer"
                  >
                    <ArrowLeft size={14} /> Voltar à pergunta 3
                  </button>
                  <span className="flex items-center gap-1">
                    <ShieldCheck size={14} className="text-primary" /> Dados 100% seguros
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Regras da Campanha — colapsável */}
      {!submitted && <RegrasSection parceiro={campanha?.nome_parceiro} />}

      {/* Footer */}
      <footer className="border-t border-border/40 py-4 px-4 text-center text-xs text-muted-foreground">
        <div className="container max-w-4xl flex flex-col sm:flex-row items-center justify-between gap-2">
          <span>Juliano Wagner · Consultor Patrimonial ANCORD & Ademicon</span>
          <span className="flex items-center gap-1">
            <Sparkles size={11} className="text-primary" />
            Parceiro: {campanha?.nome_parceiro}
          </span>
        </div>
      </footer>
    </div>
  );
}

// ── Regras da Campanha (colapsável)
const REGRAS = [
  {
    icon: Users,
    num: "01",
    title: "Participação Única",
    text: "Só é permitida uma (1) participação por número de WhatsApp. Entradas duplicadas não serão contabilizadas."
  },
  {
    icon: UserCheck,
    num: "02",
    title: "Dados Válidos",
    text: "Preencha o formulário com seus dados reais. O contato com o vencedor será feito exclusivamente pelo telefone cadastrado."
  },
  {
    icon: MapPin,
    num: "03",
    title: "Apenas no Local",
    text: "Ação exclusiva para clientes presencialmente na loja parceira. Links compartilhados em grupos serão desclassificados."
  },
  {
    icon: Gift,
    num: "04",
    title: "Resgate do Prêmio",
    text: "O vencedor deverá comprovar ser o titular do número cadastrado e terá 7 dias corridos para retirar a premiação."
  }
];

function RegrasSection({ parceiro }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-border/40 bg-background/60">
      <div className="container max-w-2xl mx-auto px-4 py-3">
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors group"
        >
          <span className="flex items-center gap-1.5">
            <ShieldCheck size={13} className="text-primary" />
            <span className="font-medium uppercase tracking-wider">Regulamento da Campanha</span>
            {parceiro && <span className="text-muted-foreground/60">· {parceiro}</span>}
          </span>
          <ChevronDown
            size={14}
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 pb-1">
                {REGRAS.map((r) => {
                  const Icon = r.icon;
                  return (
                    <div
                      key={r.num}
                      className="flex items-start gap-2.5 bg-card-gradient border border-border/50 rounded-xl p-3"
                    >
                      <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Icon size={13} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-[10px] font-mono text-primary/60 uppercase tracking-widest mb-0.5">
                          Regra {r.num}
                        </p>
                        <p className="text-xs font-semibold text-foreground mb-0.5">{r.title}</p>
                        <p className="text-xs text-muted-foreground leading-relaxed">{r.text}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function QuestionStep({ question, stepLabel, pct, selectedKey, onSelect, onBack, prize }) {
  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="bg-card-gradient border border-primary/20 shadow-gold rounded-2xl p-6 sm:p-10"
    >
      <div className="flex items-center justify-between mb-6">
        <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-primary font-semibold">
          {stepLabel} · Pergunta Rápida
        </span>
        <span className="text-xs text-muted-foreground font-mono">{pct}</span>
      </div>

      <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-light mb-8 leading-snug">
        {question.question.split(" ").slice(0, -2).join(" ")}{" "}
        <span className="italic text-gold-gradient font-normal">
          {question.question.split(" ").slice(-2).join(" ")}
        </span>
      </h2>

      <div className="space-y-4 mb-8">
        {question.options.map((opt) => {
          const isSelected = selectedKey?.startsWith(opt.key);
          return (
            <button
              key={opt.key}
              id={`btn-opcao-${question.id}-${opt.key}`}
              type="button"
              onClick={() => onSelect(opt.key, opt.label)}
              className={`w-full text-left p-5 rounded-xl border transition-all duration-200 flex items-start gap-4 ${
                isSelected
                  ? "border-primary bg-primary/10 shadow-gold ring-1 ring-primary/40"
                  : "border-border bg-background/50 hover:border-primary/50 hover:bg-background/80"
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center font-mono font-bold text-sm shrink-0 transition-colors ${
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-white/5 text-muted-foreground border border-border"
                }`}
              >
                {opt.key}
              </div>
              <div className="flex-1">
                <div className="font-medium text-foreground text-base sm:text-lg mb-1">{opt.label}</div>
                <div className="text-xs sm:text-sm text-muted-foreground">{opt.sub}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground pt-4 border-t border-border/40">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
          >
            <ArrowLeft size={14} /> Voltar
          </button>
        ) : (
          <span className="flex items-center gap-1.5">
            <Gift size={14} className="text-primary" /> {prize}
          </span>
        )}
        <span>Toque para avançar</span>
      </div>
    </motion.div>
  );
}
